<template>
  <div class="pa-1">
    <v-layout align-center justify-end class="tw-gap-3 pb-3">
      <download-button
        :data="users"
        label="Download Users"
        fileNamePrefix="users"
        :headers="exportHeaders"
        :headerMapping="exportHeaderMapping"
        @download-complete="onDownloadComplete"
        iconClass="tw-text-primary-300 hover:tw-text-primary-400"
      />
      <AddUser v-on:complete="inviteAdded" :has-sso="hasSso" v-if="checkScope(scopeLiterals.UsersCreate)" />
    </v-layout>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="users"
      :pagination.sync="pagination"
      :loading="isLoading > 0"
      select-all
      item-key="name"
      class="tw-border tw-border-neutral-100 tw-rounded-lg tw-overflow-hidden"
    >
      <v-progress-linear slot="progress" color="accent" height="2" indeterminate></v-progress-linear>
      <template slot="headers" slot-scope="props">
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="[
              'column sortable text-xs-' + header.align,
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : '',
            ]"
            @click="changeSort(header.value)"
          >
            <v-icon small>arrow_upward</v-icon>
            {{ header.text }}
          </th>
          <th></th>
        </tr>
      </template>
      <template slot="items" slot-scope="props">
        <tr :active="props.selected" @click="props.selected = !props.selected">
          <td>{{ props.item.name }}</td>
          <td>
            {{ props.item.emailOrRemoteId }}
            <span
              v-if="!props.item.email && props.item.remoteIdType"
              class="tw-ml-1 tw-text-xs tw-uppercase tw-text-neutral-400"
              :title="'Remote id from the user\'s ' + props.item.remoteIdType + ' identity'"
              >{{ props.item.remoteIdType }}</span
            >
          </td>
          <td>{{ props.item.role.map((r) => r.name).join(', ') }}</td>
          <td>{{ formatLastLogin(props.item.lastLogin) }}</td>
          <td class="text-xs-right">
            <RevokeUser v-on:completed="refresh" :user="props.item" v-if="checkScope(scopeLiterals.UsersDelete)" />
            <EditUser v-on:completed="refresh" :user="props.item" v-if="checkScope(scopeLiterals.UsersUpdate)" />
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import moment from 'moment-timezone';

import { baConfig } from '../../../config';
import { PrincipalsApi } from '../../../generated/core-svc';
import DownloadButton from '../common/DownloadButton.vue';
import AddUser from './AddUser.vue';
import EditUser from './EditUser.vue';
import RevokeUser from './RevokeUser.vue';
import { principalToUserRow } from './userIdentity';

export default {
  props: [],
  data() {
    return {
      users: [],
      orgSso: null,
      pagination: {
        sortBy: 'sortName',
      },
      selected: [],
      isLoading: 0,
    };
  },
  apollo: {
    orgSso: {
      query: gql`
        query GetOrgSso($id: ID!) {
          org(id: $id) {
            id
            sso {
              idpIssuer
            }
          }
        }
      `,
      variables() {
        return {
          id: this.$store.state.currentOrg.id,
        };
      },
      update(data) {
        return data.org?.sso ?? null;
      },
    },
  },
  computed: {
    hasSso() {
      return !!this.orgSso?.idpIssuer;
    },
    headers() {
      const baseHeaders = [
        {
          text: 'Name',
          align: 'left',
          value: 'sortName',
        },
        {
          text: 'Email / Remote Id',
          align: 'left',
          value: 'emailOrRemoteId',
        },
        {
          text: 'Role',
          align: 'left',
          value: 'role',
        },
        {
          text: 'Last Login',
          align: 'left',
          value: 'lastLogin',
        },
      ];

      return baseHeaders;
    },
    exportHeaders() {
      const baseExportHeaders = ['id', 'name', 'email', 'remoteId', 'role', 'assertedIdentity', 'walletIds'];
      return baseExportHeaders;
    },
    exportHeaderMapping() {
      return {
        id: 'ID',
        name: 'Name',
        email: 'Email',
        remoteId: 'Remote ID',
        role: 'Role',
        assertedIdentity: 'Asserted Identity',
        walletIds: 'Wallet IDs',
      };
    },
  },
  mounted() {
    this.fetchUsers();
  },
  methods: {
    async fetchUsers() {
      this.isLoading++;
      try {
        const principalsApi = new PrincipalsApi(undefined, `${baConfig.api3Url}/v3`);
        const response = await principalsApi.getOrgPrincipalsAggregated(
          this.$store.state.currentOrg.id,
          undefined,
          1000,
          {
            withCredentials: true,
          }
        );

        this.users = (response.data.items || []).map(principalToUserRow);
      } catch (error) {
        console.error('Failed to fetch users from core-svc:', error);
      } finally {
        this.isLoading--;
      }
    },
    onDownloadComplete() {
      console.log('Users download completed');
      // You can add additional logic here if needed, such as showing a notification
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
    inviteAdded() {
      this.$emit('switch', 'invitations');
      this.refresh();
    },
    refresh() {
      this.users = [];
      this.fetchUsers();
    },
    formatLastLogin(isoString) {
      if (!isoString) {
        return '';
      }
      const tz = this.$store.state.currentOrg.timezone;
      return moment(isoString).tz(tz).format('MM-DD-YYYY HH:mm:ss');
    },
  },
  components: {
    AddUser: AddUser,
    RevokeUser: RevokeUser,
    EditUser: EditUser,
    DownloadButton,
  },
};
</script>
